/**
 * A brain dump split into separate thoughts (CER-225 phase 1): what
 * split-brain-dump returns, read whole or refused, so a broken answer never
 * becomes a half-saved dump. Each thought is then saved and filed as an
 * ordinary capture.
 */
export const BRAIN_DUMP_TRANSCRIPT_MAX = 30_000;
export const BRAIN_DUMP_ITEMS_MAX = 100;
export const BRAIN_DUMP_ITEM_MAX = 2000;

export function readBrainDumpItems(raw: unknown): string[] | null {
  if (typeof raw !== 'object' || raw === null || !Array.isArray((raw as { items?: unknown }).items)) return null;
  const items = (raw as { items: unknown[] }).items;
  if (items.length === 0 || items.length > BRAIN_DUMP_ITEMS_MAX) return null;
  const read: string[] = [];
  for (const item of items) {
    if (typeof item !== 'string') return null;
    const text = item.replace(/\s+/g, ' ').trim();
    if (!text || text.length > BRAIN_DUMP_ITEM_MAX) return null;
    read.push(text);
  }
  return read;
}

/**
 * Phase 2 (CER-236): the one follow-up a thought may get right after a dump,
 * in fixed words. The classifier says which thoughts lack something; code
 * asks. At most BRAIN_DUMP_QUESTIONS_MAX per dump, in the order said.
 */
export type BrainDumpAsk = 'next_step' | 'who' | 'when';
export const BRAIN_DUMP_QUESTIONS_MAX = 5;
export const BRAIN_DUMP_QUESTION_TEXT: Record<BrainDumpAsk, string> = {
  next_step: "What's the very next step?",
  who: 'Who is this about?',
  when: 'When does this need to happen?',
};
const FOLD_LABEL: Record<BrainDumpAsk, string> = { next_step: 'Next step', who: 'With', when: 'When' };

export interface BrainDumpQuestion {
  /** Which thought, by its place in the list. */
  index: number;
  ask: BrainDumpAsk;
}

export interface BrainDumpSplit {
  items: string[];
  questions: BrainDumpQuestion[];
  /** The classifier could not be asked: every thought is saved as said, and the app says so. */
  questionsUnavailable: boolean;
}

/** split-brain-dump's whole answer; null when it is not one. */
export function readBrainDumpSplit(raw: unknown): BrainDumpSplit | null {
  const items = readBrainDumpItems(raw);
  if (!items) return null;
  const r = raw as { questions?: unknown; questions_unavailable?: unknown };
  if (!Array.isArray(r.questions) || typeof r.questions_unavailable !== 'boolean') return null;
  const seen = new Set<number>();
  const questions: BrainDumpQuestion[] = [];
  for (const q of r.questions) {
    if (typeof q !== 'object' || q === null) return null;
    const { index, ask } = q as { index?: unknown; ask?: unknown };
    if (typeof index !== 'number' || !Number.isInteger(index) || index < 0 || index >= items.length || seen.has(index)) return null;
    if (ask !== 'next_step' && ask !== 'who' && ask !== 'when') return null;
    seen.add(index);
    questions.push({ index, ask });
  }
  if (questions.length > BRAIN_DUMP_QUESTIONS_MAX) return null;
  return { items, questions, questionsUnavailable: r.questions_unavailable };
}

/** A thought with its follow-up answer folded in, so filing reads both: "Call him about the contract. With: Dan". */
export function foldAnswer(thought: string, ask: BrainDumpAsk, answer: string): string {
  const said = answer.replace(/\s+/g, ' ').trim();
  if (!said) return thought;
  const ended = /[.!?]$/.test(thought) ? thought : `${thought}.`;
  return `${ended} ${FOLD_LABEL[ask]}: ${said}`;
}
